import React, { useState, useMemo } from 'react';
import { Property, AppUser, UserRole } from '../../types';
import { generateMaskedCode } from '../../utils/propertyMappers';
import { CopyFallbackModal } from '../CopyFallbackModal';
import { PropertyCardImage } from './card/PropertyCardImage';
import { PropertyCardStatus } from './card/PropertyCardStatus';
import { PropertyCardInfo } from './card/PropertyCardInfo';
import { PropertyCardFooter } from './card/PropertyCardFooter';
import { HeartIconSolid, HeartIconOutline } from '../Icons';
import { getResolvedCommissionRate } from '../../utils/commissionUtils';
import { isDaviviendaPromoProperty } from '../../utils/clientLocationHelper';

interface PropertyCardProps {
  property: Property;
  isSelected: boolean;
  onView: () => void;
  onShare: () => void;
  onToggleSelect: () => void;
  isCompact: boolean;
  disabled?: boolean;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  source: 'ciencuadras' | 'aliados' | 'brokers';
  appUser: AppUser | null;
  onInterested: (property: Property) => void;
  isSelectionModeActive?: boolean;
  inmobiliariasConfig?: Record<string, any>;
  hideSpecialStatus?: boolean;
  isLiked?: boolean;
  onToggleLike?: () => void;
  commentCount?: number;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, isSelected, onView, onShare, onToggleSelect, isCompact, isHighlighted, onMouseEnter, onMouseLeave, source, appUser, isSelectionModeActive, inmobiliariasConfig = {}, hideSpecialStatus,
  isLiked, onToggleLike, commentCount
}) => {
  const [targetStatus, setTargetStatus] = useState<string | null>(null);
  const [copyModalState, setCopyModalState] = useState<{ isOpen: boolean; text: string; title: string } | null>(null);
  
  const isTargetEliminado = targetStatus === 'Eliminado';
  const isDeleted = property.status === 'Eliminado' || isTargetEliminado;
  
  const isUpdated = !isDeleted && (property.status === 'Actualizado' || property.status === 'ActualizadoCodigo');
  const isInactive = !isDeleted && !isUpdated && property.status !== 'Activo';
  const isMarketInactive = isDeleted || isInactive || (isUpdated && targetStatus === 'Vendido');

  const isNewlyAdded = useMemo(() => {
    if (hideSpecialStatus) return false;
    const rawVal = property.fecha_agregado_nuevo || property.fechaAgregadoNuevo || (property as any).fecha_creacion;
    if (!rawVal) return false;

    let timestamp = 0;
    if (typeof rawVal === 'number') timestamp = rawVal > 1e11 ? rawVal : rawVal * 1000;
    else if (rawVal instanceof Date) timestamp = rawVal.getTime();
    else if (typeof (rawVal as any).toMillis === 'function') timestamp = (rawVal as any).toMillis();
    else if (typeof (rawVal as any).toDate === 'function') timestamp = (rawVal as any).toDate().getTime();
    else if ((rawVal as any)._seconds != null) timestamp = (rawVal as any)._seconds * 1000;
    else if ((rawVal as any).seconds != null) timestamp = (rawVal as any).seconds * 1000;
    else if (typeof rawVal === 'string') timestamp = Date.parse(rawVal) || 0;

    if (!timestamp) return false;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return timestamp >= sevenDaysAgo;
  }, [property.fecha_agregado_nuevo, property.fechaAgregadoNuevo, (property as any).fecha_creacion, hideSpecialStatus]);

  const hasCommissionOverride = useMemo(() => {
    if (hideSpecialStatus || appUser?.role === UserRole.CLIENTE) return false;
    return (property.comision_override_broker != null || property.comision_override_premium != null || property.comision_override_interno != null);
  }, [property, appUser, hideSpecialStatus]);

  const displayCode = useMemo(() => (appUser?.role === UserRole.BROKER_PREMIUM) ? (property.codigo_enmascarado || generateMaskedCode(property.id)) : property.codigo, [property, appUser]);
  const resolvedCommissionRate = useMemo(() => getResolvedCommissionRate(property, appUser, inmobiliariasConfig), [property, appUser, inmobiliariasConfig]);

  const cardBorderClass = isHighlighted ? 'border border-sky-400 ring-2 ring-sky-400/20 shadow-lg' : 'border border-gray-200';

  return (
    <>
      <div 
        onClick={() => isSelectionModeActive ? onToggleSelect() : onView()} 
        onMouseEnter={onMouseEnter} 
        onMouseLeave={onMouseLeave} 
        className={`relative bg-white rounded-2xl flex flex-col overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md h-full ${isCompact ? '' : 'hover:-translate-y-1'} ${cardBorderClass} ${isMarketInactive ? 'filter grayscale opacity-60' : ''} ${isSelectionModeActive ? 'cursor-pointer hover:shadow-2xl' : ''} ${isSelected && isSelectionModeActive ? 'ring-4 ring-green-500 bg-green-50' : ''}`}
      >
        <div className="relative w-full aspect-[4/3] overflow-hidden flex-shrink-0 group">
          {!isSelectionModeActive && (
            <PropertyCardStatus 
                isDeleted={isDeleted} 
                isUpdated={isUpdated} 
                isInactive={isInactive} 
                targetStatus={targetStatus} 
                status={property.status} 
                isNewlyAdded={!hideSpecialStatus && isNewlyAdded && !isMarketInactive}
                es_preventa={!hideSpecialStatus && property.es_preventa}
                hasCommissionOverride={hasCommissionOverride}
                tiene_oferta={!hideSpecialStatus && property.tiene_oferta}
                valor_oferta={property.valor_oferta}
                hasDaviviendaCreditPromo={!hideSpecialStatus && isDaviviendaPromoProperty(property.ciudad, property.tipoinmueble)}
                isClientRole={appUser?.role === UserRole.CLIENTE}
                tags={property.tag || property.tags}
            />
          )}
          <PropertyCardImage initialImages={property.images} propertyCode={property.codigo} source={source} isUpdated={isUpdated} codigoActual={property.codigo_actual} isSelected={isSelected} isSelectionModeActive={!!isSelectionModeActive} onStatusDetected={setTargetStatus} />
          
          {!isSelectionModeActive && onToggleLike && (
              <button 
                  onClick={(e) => { e.stopPropagation(); onToggleLike(); }}
                  className="absolute top-2 right-2 z-[40] p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-all active:scale-90"
                  aria-label={isLiked ? "Quitar de favoritos" : "Añadir a favoritos"}
              >
                  {isLiked ? (
                      <HeartIconSolid className="w-5 h-5 text-red-500" />
                  ) : (
                      <HeartIconOutline className="w-5 h-5 text-gray-400 hover:text-red-500" />
                  )}
              </button>
          )}
        </div>
        
        <PropertyCardInfo 
          property={property} 
          isCompact={isCompact} 
          appUser={appUser} 
          resolvedCommissionRate={resolvedCommissionRate} 
          displayCode={displayCode} 
          isUpdated={isUpdated} 
          setCopyModalState={setCopyModalState} 
          commentCount={commentCount}
        />

        {!isSelectionModeActive && (
          <div className="px-4 pb-4">
            <PropertyCardFooter onView={onView} onShare={onShare} isMarketInactive={isMarketInactive} />
          </div>
        )}
      </div>
      {copyModalState && <CopyFallbackModal isOpen={copyModalState.isOpen} onClose={() => setCopyModalState(null)} textToCopy={copyModalState.text} title={copyModalState.title} />}
    </>
  );
};
